import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import ResourceTable, { Badge } from "@/admin/ResourceTable";
import { ScanLine as ScanIcon, QrCode, Plus, Pencil, X as XIcon } from "lucide-react";
import BatchQR from "@/admin/BatchQR";
import NewBatchModal from "@/admin/NewBatchModal";
import EditBatchModal from "@/admin/EditBatchModal";
import BatchDetail from "@/admin/BatchDetail";

const money = (n) => "₹" + (n ?? 0).toLocaleString("en-IN");
const dt = (s) => (s ? new Date(s).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—");
const statusTone = (s) => ({ active: "green", used: "blue", blocked: "red", expired: "amber", successful: "green", failed: "red", pending: "amber", initiated: "gray", reversed: "amber", success: "green", invalid: "red", import_exception: "amber", draft: "gray", approved: "blue", launched: "green" }[s] || "gray");

export function Painters() {
  const [sp, setSp] = useSearchParams();
  const dealerId = sp.get("dealer_id") || "";
  const dealerName = sp.get("dealer_name") || "";
  return <>
    {dealerId && (
      <div data-testid="painters-dealer-filter" className="mb-3 inline-flex items-center gap-2 bg-[#FDF2F2] text-[#8B0000] text-sm font-semibold px-3 py-1.5 rounded-full">
        Linked to dealer: {dealerName || dealerId}
        <button data-testid="painters-dealer-filter-clear" onClick={() => setSp({})}><XIcon className="h-3.5 w-3.5" /></button>
      </div>
    )}
    <ResourceTable title="Users & Painters" subtitle="Painters & contractors identified by unique WhatsApp mobile number" endpoint="/loyalty-users" exportEntity="loyalty-users" withPeriod
    extraParams={dealerId ? { dealer_id: dealerId } : {}}
    filters={[
      { key: "role", label: "All Roles", options: [{ value: "painter", label: "Painter" }, { value: "contractor", label: "Contractor" }, { value: "dealer", label: "Dealer" }] },
      { key: "status", label: "All Status", options: [{ value: "active", label: "Active" }, { value: "import_exception", label: "Import Exception" }] },
    ]}
    columns={[
      { key: "name", label: "Name", render: (r) => <span className="font-semibold text-gray-900">{r.name}</span> },
      { key: "mobile", label: "Mobile no", render: (r) => <span className="font-mono">{r.mobile}</span> },
      { key: "role", label: "Type", render: (r) => <Badge tone={r.role === "contractor" ? "blue" : "crimson"}>{r.role}</Badge> },
      { key: "wallet_balance", label: "Wallet", csv: (r) => (r.wallet_balance ?? r.opening_points) || 0, render: (r) => <span className="font-bold text-[#8B0000]">{(r.wallet_balance ?? r.opening_points) || 0} pts</span> },
      { key: "lifetime_earned", label: "Lifetime Earned", render: (r) => <span className="font-semibold text-gray-800">{(r.lifetime_earned || 0).toLocaleString("en-IN")} pts</span> },
      { key: "redeemed_points", label: "Redeemed", render: (r) => <span className="text-gray-600">{(r.redeemed_points || 0).toLocaleString("en-IN")} pts</span> },
      { key: "last_dealer_name", label: "Last Dealer", render: (r) => r.last_dealer_name || <Badge tone={r.dealer_map_status === "exception" ? "amber" : "gray"}>{r.dealer_map_status || "unmapped"}</Badge> },
      { key: "total_dbt", label: "Total DBT", render: (r) => <span className="font-semibold">₹{(r.total_dbt || 0).toLocaleString("en-IN")}</span> },
      { key: "gifts", label: "Gifts", render: (r) => r.gifts || 0 },
      { key: "depot", label: "Depot", render: (r) => r.depot || "—" },
      { key: "zone", label: "Zone", render: (r) => r.zone || "—" },
      { key: "sales_person", label: "Sales Person", render: (r) => r.sales_person || "—" },
    ]} />
  </>;
}

export function Dealers() {
  return <ResourceTable title="Dealers" subtitle="Attribution network — normalized dealer codes" endpoint="/dealers" exportEntity="dealers" withPeriod
    filters={[{ key: "status", label: "All Status", options: [{ value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }] }]}
    columns={[
      { key: "code", label: "Code", render: (r) => <span className="font-mono text-xs">{r.code}</span> },
      { key: "name", label: "Name", render: (r) => <span className="font-semibold text-gray-900">{r.name}</span> },
      { key: "zone", label: "Zone", render: (r) => r.zone || "—" },
      { key: "depot", label: "Depot", render: (r) => r.depot || "—" },
      { key: "state", label: "State" },
      { key: "city", label: "City" },
      { key: "zip", label: "Zip code", render: (r) => r.pin_missing ? <Badge tone="amber">Missing</Badge> : (r.zip || r.pin) },
      { key: "painters_scanned", label: "Painters Scanned", render: (r) => <span className="font-bold text-gray-900">{r.painters_scanned || 0}</span> },
      { key: "members_count", label: "Painters / Contractors", render: (r) => (
        <Link data-testid={`dealer-members-${r.id}`} to={`/admin/painters?dealer_id=${r.id}&dealer_name=${encodeURIComponent(r.name)}`}
          className="inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded-full bg-[#FDF2F2] text-[#8B0000] font-bold hover:bg-[#8B0000] hover:text-white transition-colors">{r.members_count || 0}</Link>
      ) },
      { key: "gifts_issued", label: "Gifts Issued", render: (r) => r.gifts_issued || 0 },
      { key: "sales_person", label: "Sales Person", render: (r) => r.sales_person || "—" },
    ]} />;
}

export function Products() {
  return <ResourceTable title="Products" subtitle="Product scan performance by SKU" endpoint="/reports/product-scans" exportEntity="products" withPeriod
    filters={[{ key: "category", label: "All Categories", options: [{ value: "LUXURY", label: "Luxury" }, { value: "Premium", label: "Premium" }, { value: "Budget", label: "Budget" }] }]}
    columns={[
      { key: "scan", label: "Scan", noExport: true, render: (r) => <span className="inline-flex h-7 w-7 items-center justify-center rounded bg-[#FDF2F2] text-[#8B0000]"><ScanIcon /></span> },
      { key: "code", label: "Code", render: (r) => <span className="font-mono text-xs">{r.code}</span> },
      { key: "name", label: "Product Name", render: (r) => <span className="font-semibold text-gray-900">{r.name}</span> },
      { key: "token_name", label: "Token Name", render: (r) => r.token_name ? <span data-testid={`product-token-${r.id}`} className="font-semibold text-gray-800">{r.token_name}</span> : <span className="text-gray-400">—</span> },
      { key: "pack_size", label: "Pack", render: (r) => <span className="font-mono">{r.pack_size} L</span> },
      { key: "category", label: "Category" },
      { key: "range", label: "Range" },
      { key: "scans", label: "Scans", render: (r) => <span className="font-bold text-gray-900">{r.scans || 0}</span> },
      { key: "total_dbt", label: "Total DBT", render: (r) => <span className="font-semibold">₹{(r.total_dbt || 0).toLocaleString("en-IN")}</span> },
      { key: "total_issued", label: "Total Issued", render: (r) => <span className="font-bold text-[#8B0000]">{(r.total_issued || 0).toLocaleString("en-IN")} pts</span> },
      { key: "qr_pending", label: "QR Pending", render: (r) => <Badge tone={r.qr_pending > 0 ? "amber" : "gray"}>{r.qr_pending || 0}</Badge> },
    ]} />;
}

export function Scans() {
  return <ResourceTable title="Scan Logs" subtitle="Real-time coupon validation & redemption stream" endpoint="/scans" exportEntity="scans"
    filters={[{ key: "result", label: "All Results", options: [{ value: "success", label: "Success" }, { value: "invalid", label: "Invalid" }] }]}
    columns={[
      { key: "code", label: "Coupon", render: (r) => <span className="font-mono text-xs">{r.code}</span> },
      { key: "result", label: "Result", render: (r) => <Badge tone={statusTone(r.result)}>{r.result}</Badge> },
      { key: "mobile", label: "Mobile", render: (r) => r.mobile || "—" },
      { key: "product_name", label: "Product", render: (r) => r.product_name || "—" },
      { key: "reward_points", label: "Points", render: (r) => r.reward_points || "—" },
      { key: "dealer_name", label: "Dealer", render: (r) => r.dealer_name || "—" },
      { key: "created_at", label: "Time", render: (r) => <span className="text-xs">{dt(r.created_at)}</span> },
    ]} />;
}

export function Ledger() {
  return <ResourceTable title="Wallet Ledger" subtitle="Append-only ledger — balance = sum of posted entries" endpoint="/wallet-ledger" exportEntity="wallet-ledger"
    filters={[{ key: "type", label: "All Types", options: ["opening_balance", "coupon_reward", "campaign_bonus", "upi_debit", "gift_debit", "reversal", "manual_adjustment"].map((v) => ({ value: v, label: v })) }]}
    columns={[
      { key: "user_id", label: "User", render: (r) => <span className="font-mono text-xs">{r.user_id?.slice(0, 8)}</span> },
      { key: "type", label: "Type", render: (r) => <Badge tone={r.amount >= 0 ? "green" : "red"}>{r.type}</Badge> },
      { key: "amount", label: "Amount", render: (r) => <span className={`font-bold font-mono ${r.amount >= 0 ? "text-green-600" : "text-red-600"}`}>{r.amount >= 0 ? "+" : ""}{r.amount}</span> },
      { key: "note", label: "Note", render: (r) => <span className="text-xs text-gray-500">{r.note}</span> },
      { key: "data_source", label: "Source" },
      { key: "created_at", label: "Time", render: (r) => <span className="text-xs">{dt(r.created_at)}</span> },
    ]} />;
}

export function Batches() {
  const [qrBatch, setQrBatch] = useState(null);
  const [newOpen, setNewOpen] = useState(false);
  const [editBatch, setEditBatch] = useState(null);
  const [detail, setDetail] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  return (
    <>
      <ResourceTable title="Coupon Batches" subtitle="Batch-wise QR generated, scanned & active — tap a batch for its coupon details" endpoint="/coupon-batches" exportEntity="coupon-batches"
        refreshKey={refreshKey}
        headerActions={
          <button data-testid="new-batch-btn" onClick={() => setNewOpen(true)}
            className="flex items-center gap-2 bg-[#8B0000] hover:bg-[#6B0000] text-white font-bold px-4 py-2 rounded-lg text-sm">
            <Plus className="h-4 w-4" /> Generate New QR Batch
          </button>
        }
        columns={[
          { key: "name", label: "Batch", render: (r) => <button data-testid={`batch-open-${r.id}`} onClick={() => setDetail(r)} className="font-semibold text-gray-900 hover:text-[#8B0000] hover:underline text-left">{r.name}</button> },
          { key: "target_role", label: "For", render: (r) => <Badge tone={r.target_role === "contractor" ? "blue" : "crimson"}>{r.target_role || "painter"}</Badge> },
          { key: "product_name", label: "Product", render: (r) => r.product_name || "—" },
          { key: "qr_generated", label: "QR Generated", render: (r) => <button onClick={() => setDetail(r)} className="font-bold text-gray-900">{r.qr_generated ?? r.generated}</button> },
          { key: "qr_scanned", label: "Scanned", render: (r) => <button data-testid={`batch-scanned-${r.id}`} onClick={() => setDetail({ ...r, _status: "used" })} className="font-bold text-blue-700 hover:underline">{r.qr_scanned || 0}</button> },
          { key: "qr_active", label: "Active", render: (r) => <button data-testid={`batch-active-${r.id}`} onClick={() => setDetail({ ...r, _status: "active" })} className="font-bold text-green-700 hover:underline">{r.qr_active || 0}</button> },
          { key: "reward_points", label: "Reward Pts", render: (r) => <span className="font-bold text-[#8B0000]">{r.reward_points}</span> },
          { key: "reward_value_rs", label: "Value ₹", render: (r) => <span className="font-semibold">₹{(r.reward_value_rs ?? 0).toLocaleString("en-IN")}</span> },
          { key: "status", label: "Status", render: (r) => <Badge tone={statusTone(r.status)}>{r.status}</Badge> },
          { key: "created_at", label: "Created", render: (r) => <span className="text-xs">{dt(r.created_at)}</span> },
          { key: "edit", label: "Edit", noExport: true, render: (r) => (
            <button data-testid={`batch-edit-btn-${r.id}`} onClick={() => setEditBatch(r)}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-600 hover:text-[#8B0000]">
              <Pencil className="h-4 w-4" /> Edit
            </button>
          ) },
          { key: "qr", label: "QR Codes", noExport: true, render: (r) => (
            <button data-testid={`batch-qr-btn-${r.id}`} onClick={() => setQrBatch(r)}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#8B0000] hover:underline">
              <QrCode className="h-4 w-4" /> View / Print
            </button>
          ) },
        ]} />
      {newOpen && <NewBatchModal onClose={() => setNewOpen(false)}
        onCreated={(b) => { setNewOpen(false); setRefreshKey((k) => k + 1); setQrBatch(b); }} />}
      {editBatch && <EditBatchModal batch={editBatch} onClose={() => setEditBatch(null)}
        onSaved={() => { setEditBatch(null); setRefreshKey((k) => k + 1); }} />}
      {qrBatch && <BatchQR batch={qrBatch} onClose={() => setQrBatch(null)} />}
      {detail && <BatchDetail batch={detail} initialStatus={detail._status || ""} onClose={() => setDetail(null)} />}
    </>
  );
}

export function AuditLogs() {
  return <ResourceTable title="Audit Logs" subtitle="Every privileged action, recorded" endpoint="/audit-logs" searchable={false}
    columns={[
      { key: "user_email", label: "User", render: (r) => <span className="font-medium">{r.user_email}</span> },
      { key: "role", label: "Role" },
      { key: "action", label: "Action", render: (r) => <Badge tone="crimson">{r.action}</Badge> },
      { key: "entity", label: "Entity" },
      { key: "entity_id", label: "Ref", render: (r) => <span className="font-mono text-xs">{r.entity_id?.slice(0, 8) || "—"}</span> },
      { key: "created_at", label: "Time", render: (r) => <span className="text-xs">{dt(r.created_at)}</span> },
    ]} />;
}

export function Reports() {
  const entities = [
    ["scans", "Scan Logs"], ["wallet-ledger", "Wallet Ledger"], ["upi", "UPI Redemptions"],
    ["dealers", "Dealers"], ["products", "Products"], ["loyalty-users", "Painters"],
    ["gift-orders", "Gift Orders"], ["campaigns", "Campaigns"],
  ];
  const dl = async (e) => {
    const { api } = await import("@/lib/api");
    const res = await api.get("/reports/export", { params: { entity: e }, responseType: "blob" });
    const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = `${e}.csv`; a.click();
  };
  return (
    <div>
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-gray-900">Reports & Exports</h1>
      <p className="text-sm text-gray-500 mt-1 mb-6">Server-side data exports — CSV downloads for every dataset</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {entities.map(([e, label]) => (
          <button key={e} data-testid={`report-${e}`} onClick={() => dl(e)}
            className="bg-white border border-gray-200 rounded-xl p-5 text-left hover:border-[#8B0000] hover:shadow-md transition-all">
            <div className="font-semibold text-gray-900">{label}</div>
            <div className="mt-1 text-xs text-[#8B0000] font-semibold">Download CSV →</div>
          </button>
        ))}
      </div>
    </div>
  );
}

export function ERP() {
  const [view, setView] = React.useState("business");
  const [d, setD] = React.useState(null);
  React.useEffect(() => { import("@/lib/api").then(({ api }) => api.get("/erp/dashboards", { params: { view } }).then((r) => setD(r.data))); }, [view]);
  return (
    <div>
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-gray-900">ERP Dashboards</h1>
      <p className="text-sm text-gray-500 mt-1 mb-4">Report-only layer. CSV/API ingestion ready.</p>
      <div className="mb-4 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-4 py-2 text-sm">{d?.note}</div>
      <div className="flex gap-2 mb-4">
        {["business", "sales", "dealer", "product"].map((v) => (
          <button key={v} data-testid={`erp-view-${v}`} onClick={() => setView(v)}
            className={`px-3 py-1.5 rounded-lg text-sm font-semibold capitalize ${view === v ? "bg-[#8B0000] text-white" : "bg-white border border-gray-200 text-gray-600"}`}>{v}</button>
        ))}
      </div>
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm text-left">
          <thead><tr className="bg-gray-50 border-b">{d?.rows?.[0] && Object.keys(d.rows[0]).map((c) => <th key={c} className="py-3 px-4 uppercase text-[11px] tracking-wider text-gray-600 font-bold">{c}</th>)}</tr></thead>
          <tbody>{d?.rows?.map((row, i) => <tr key={i} className="border-b border-gray-100">{Object.values(row).map((v, j) => <td key={j} className="py-3 px-4 text-gray-700">{String(v)}</td>)}</tr>)}</tbody>
        </table>
      </div>
    </div>
  );
}

export function Fraud() {
  const [flags, setFlags] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const load = () => import("@/lib/api").then(({ api }) => api.get("/fraud/flags", { params: { status: "open" } }).then((r) => setFlags(r.data)));
  React.useEffect(() => { load(); }, []);
  const scan = async () => { setBusy(true); const { api } = await import("@/lib/api"); const { toast } = await import("sonner"); const r = await api.post("/fraud/scan"); toast.success(`${r.data.new_flags} new flags detected`); await load(); setBusy(false); };
  const resolve = async (id) => { const { api } = await import("@/lib/api"); await api.patch(`/fraud/flags/${id}`, { status: "resolved" }); load(); };
  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div><h1 className="font-display text-2xl sm:text-3xl font-bold text-gray-900">Fraud & Suspicious Activity</h1>
          <p className="text-sm text-gray-500 mt-1">Flagged for review — never auto-blocks genuine users</p></div>
        <button data-testid="run-fraud-scan" onClick={scan} disabled={busy} className="bg-[#8B0000] hover:bg-[#6B0000] text-white font-bold px-4 py-2.5 rounded-lg disabled:opacity-60">Run Detection Scan</button>
      </div>
      <div className="space-y-3">
        {flags.length === 0 && <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-gray-400">No open flags. Run a detection scan.</div>}
        {flags.map((f) => (
          <div key={f.id} className="bg-white border border-gray-200 rounded-xl p-4 flex items-center justify-between" data-testid={`flag-${f.id}`}>
            <div><Badge tone="amber">{f.type}</Badge><p className="mt-1.5 text-sm text-gray-700">{f.detail}</p></div>
            <button onClick={() => resolve(f.id)} data-testid={`resolve-${f.id}`} className="text-sm font-semibold text-[#8B0000] hover:underline">Mark Reviewed</button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AdminUsers() {
  const [users, setUsers] = React.useState([]);
  React.useEffect(() => { import("@/lib/api").then(({ api }) => api.get("/auth/users").then((r) => setUsers(r.data))); }, []);
  return (
    <div>
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-gray-900">User Management</h1>
      <p className="text-sm text-gray-500 mt-1 mb-5">Portal users & their RBAC roles</p>
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <table className="w-full text-sm text-left">
          <thead><tr className="bg-gray-50 border-b"><th className="py-3 px-4 text-[11px] uppercase tracking-wider text-gray-600 font-bold">Name</th><th className="py-3 px-4 text-[11px] uppercase tracking-wider text-gray-600 font-bold">Email</th><th className="py-3 px-4 text-[11px] uppercase tracking-wider text-gray-600 font-bold">Role</th><th className="py-3 px-4 text-[11px] uppercase tracking-wider text-gray-600 font-bold">Status</th></tr></thead>
          <tbody>{users.map((u) => (
            <tr key={u.id} className="border-b border-gray-100" data-testid={`admin-user-${u.id}`}>
              <td className="py-3 px-4 font-semibold text-gray-900">{u.name}</td>
              <td className="py-3 px-4 text-gray-600">{u.email}</td>
              <td className="py-3 px-4"><Badge tone="crimson">{u.role}</Badge></td>
              <td className="py-3 px-4"><Badge tone={u.active ? "green" : "gray"}>{u.active ? "active" : "inactive"}</Badge></td>
            </tr>))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
